import AddCommentIcon from '@mui/icons-material/AddComment';
import LocalPoliceRounded from '@mui/icons-material/LocalPoliceRounded';
import VerifiedIcon from '@mui/icons-material/Verified';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Fragment, useState } from 'react';

import { Piece, PieceType } from '@apiContract';
import ForceActionsDrawer from '@components/MailpieceActions/ForceActionsDrawer';
import ActionButton from '@components/Table/actions/ActionButton';
import DialogCommentPieces from '@components/TablePieces/components/DialogCommentPieces';
import { useMailpiece } from '@hooks/useMailpiece';
import { MailPieceInboxType } from '@rdTypes/shared';
import { sxHorizontalScroll } from 'src/theme/shared/scrollbar';

import { useGetBulkActionsConfig } from '../hooks/useGetBulkActionsConfig';
import { useMailpieceActions } from '../hooks/useMailpieceActions';
import { CommonAction, ValidationData } from '../types';
import { ActionValidation } from './ActionValidation';

type MailpieceBulkActionsProps = {
  pieces: Piece[];
  access: MailPieceInboxType;
  pendingSearchValue?: string;
};

export const MailpieceBulkActions = (props: MailpieceBulkActionsProps) => {
  const { pieces, access, pendingSearchValue } = props;

  const [validationDialogOpen, setValidationDialogOpen] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<CommonAction | null>(null);
  const [validationData, setValidationData] = useState<ValidationData>({});
  const [actionPieces, setActionPieces] = useState<Piece[] | null>(null);
  const [forceActionsDrawerOpen, setForceActionsDrawerOpen] = useState(false);
  const [commentPiecesOpen, setCommentPiecesOpen] = useState(false);

  const availableActions = useGetBulkActionsConfig(pieces, access);
  const { hasForceActions } = useMailpieceActions(pieces, access);
  const { getForceActionTitle } = useMailpiece();

  const forceActionTitle = getForceActionTitle(pieces);
  const pieceIds = pieces.map((piece) => piece.id);

  const getTooltipTitle = (action: ReturnType<typeof useGetBulkActionsConfig>[number]): string => {
    if (action.tooltipText) return action.tooltipText;
    return typeof action.title === 'string' ? action.title : action.title();
  };

  const startAction = (actionId: CommonAction) => {
    setCurrentAction(actionId);
  };

  // TODO: The below part requires refactoring, it would be better to extend currently existing
  // "MailpieceActionButton" component with the ability to accept custom button markup and use it
  // here instead of current logic.
  if (!pieces.length) return null;

  const renderActionButton = (action: ReturnType<typeof useGetBulkActionsConfig>[number]) => {
    if (!action.Component && !action.isValidatePlaceholder) return null;

    const tooltipTitle = action.isValidatePlaceholder ? 'Validate' : getTooltipTitle(action);
    const isDisabled = action.isValidatePlaceholder || action.disabled || currentAction !== null;

    const actionButton = (
      <ActionButton
        title={tooltipTitle}
        onClick={() => startAction(action.id)}
        disabled={isDisabled}
        icon={<action.Icon />}
      />
    );

    const canShowValidation = pieces.every(
      (piece) => piece.pieceType !== PieceType.Mail && piece.lifecycleStage === pieces[0].lifecycleStage,
    );

    return (
      <Fragment key={action.id}>
        {canShowValidation && action.validationSettingName ? (
          <ActionValidation
            key={`${action.id}-${currentAction === action.id}`}
            dialogControlComponent={
              <Tooltip title="Validate Items" arrow>
                <IconButton
                  color="primary"
                  aria-label="Validate and proceed"
                  onClick={() => setValidationDialogOpen(true)}
                >
                  <VerifiedIcon />
                </IconButton>
              </Tooltip>
            }
            validationDialogOpen={validationDialogOpen}
            closeValidationDialog={() => setValidationDialogOpen(false)}
            onValidationSuccess={(data, validatedIds) => {
              setValidationData(data);
              setActionPieces(validatedIds ? pieces.filter((piece) => validatedIds.includes(piece.id)) : null);
              startAction(action.id);
            }}
            settingName={
              typeof action.validationSettingName === 'function'
                ? action.validationSettingName(pieces[0]) ?? ''
                : action.validationSettingName
            }
            pieceType={pieces[0].pieceType}
            piecesForValidate={pieces}
          >
            {actionButton}
          </ActionValidation>
        ) : (
          actionButton
        )}
      </Fragment>
    );
  };

  const activeAction = availableActions.find((action) => action.id === currentAction && action.Component);

  return (
    <>
      <Stack spacing={1} direction="row" sx={sxHorizontalScroll}>
        {availableActions.map(renderActionButton)}

        <ActionButton
          title="Add Comment"
          disabled={false}
          onClick={() => setCommentPiecesOpen(true)}
          icon={<AddCommentIcon />}
        />

        {hasForceActions && (
          <ActionButton
            title={forceActionTitle}
            disabled={currentAction !== null}
            onClick={() => setForceActionsDrawerOpen(true)}
            icon={<LocalPoliceRounded />}
          />
        )}

        <DialogCommentPieces
          open={commentPiecesOpen}
          handleClose={() => setCommentPiecesOpen(false)}
          pieceIds={pieceIds}
          hasAnyUnsentPiece={false}
        />

        <ForceActionsDrawer
          open={forceActionsDrawerOpen}
          setOpen={setForceActionsDrawerOpen}
          pieces={pieces}
          access={access}
          pendingSearchValue={pendingSearchValue}
        />
      </Stack>

      {activeAction && activeAction.Component && (
        <activeAction.Component
          mailpieces={actionPieces ?? pieces}
          actionStarted={currentAction === activeAction.id}
          onActionFinish={() => {
            setCurrentAction(null);
            setActionPieces(null);
          }}
          pendingSearchValue={pendingSearchValue}
          validationData={validationData}
        />
      )}
    </>
  );
};
